import { useRef, useEffect } from "react";
import { Navigate, useParams, Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowLeft, ArrowRight, MessageCircle, BadgePercent } from "lucide-react";
import QuoteForm from "@/components/QuoteForm";
import Marquee from "@/components/resort/Marquee";
import Experience from "@/components/resort/Experience";
import RichExperience from "@/components/resort/RichExperience";
import Gallery from "@/components/resort/Gallery";
import ForWhom from "@/components/resort/ForWhom";
import Videos from "@/components/resort/Videos";
import ResortHighlights, { ResortGallery } from "@/components/resort/ResortExtras";
import BoardPlan from "@/components/resort/BoardPlan";
import { AwardsStrip, OfficialInfo } from "@/components/resort/Official";
import { getResort, FAZZENDA_MARQUEE, FAZZENDA_VIDEOS } from "@/data/resorts";
import { DIRECT_MSG, waLink } from "@/data/site";
import { useSiteSettings } from "@/lib/settings";
import { useSEO } from "@/lib/seo";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

export default function ResortPage() {
    const { slug } = useParams();
    const location = useLocation();
    const resort = getResort(slug);
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

    useSEO({
        title: resort?.name,
        description: resort?.shortDescription,
        image: resort?.heroImage || resort?.cardImage,
        path: `/resorts/${slug}`,
    });

    const settings = useSiteSettings();

    useEffect(() => {
        const target = location.state?.scrollTo;
        if (!target) return undefined;
        const t = setTimeout(() => scrollToId(target), 500);
        return () => clearTimeout(t);
    }, [location, slug]);

    if (!resort) return <Navigate to="/" replace />;

    return (
        <>
            <section ref={ref} className="relative flex min-h-[78svh] items-end overflow-hidden bg-forest-deep" data-testid="resort-hero">
                <motion.div style={{ y }} className="absolute inset-0">
                    <motion.img
                        src={resort.heroImage}
                        alt={`Foto principal do ${resort.name}`}
                        className="h-full w-full object-cover"
                        initial={{ scale: 1.12 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 2.4, ease: EASE }}
                    />
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/30" />

                <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 pt-28 sm:px-6">
                    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
                        <Link
                            to="/"
                            data-testid="resort-back-btn"
                            className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide text-white/80 transition-colors hover:text-gold"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Voltar ao catálogo
                        </Link>
                    </motion.div>

                    {resort.logoWhite && (
                        <motion.img
                            src={resort.logoWhite}
                            alt={`Logotipo oficial do ${resort.name}`}
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
                            className="mt-6 h-12 w-auto drop-shadow-xl sm:h-16"
                            data-testid="resort-official-logo"
                        />
                    )}

                    <motion.span
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.25, ease: EASE }}
                        className="mt-5 inline-block rounded-full border border-white/25 bg-white/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gold backdrop-blur-md"
                        data-testid="resort-badge"
                    >
                        {resort.badge}
                    </motion.span>

                    <h1 className="mt-4 max-w-3xl font-serif text-4xl font-semibold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="resort-name">
                        <span className="block overflow-hidden pb-1">
                            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.35, ease: EASE }}>
                                {resort.name}
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
                        className="mt-4 flex items-center gap-2 text-sm font-medium text-stone-200"
                        data-testid="resort-location"
                    >
                        <MapPin className="h-4 w-4 text-gold" />
                        {resort.location}
                    </motion.p>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.72, ease: EASE }}
                        className="mt-3 max-w-2xl text-base leading-relaxed text-stone-200"
                    >
                        {resort.shortDescription}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.88, ease: EASE }}
                        className="mt-8 flex flex-col gap-3 sm:flex-row"
                    >
                        <button
                            type="button"
                            data-testid="resort-cta-quote"
                            onClick={() => scrollToId("cotacao-resort")}
                            className="group inline-flex items-center justify-center gap-2 rounded-full bg-sun px-8 py-4 text-sm font-semibold text-forest-deep shadow-xl shadow-amber-900/30 transition-all duration-200 hover:-translate-y-px hover:bg-gold hover:text-white active:scale-[0.98]"
                        >
                            Solicitar cotação deste resort
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <a
                            data-testid="resort-cta-whatsapp"
                            href={waLink(settings.whatsapp_primary, `${DIRECT_MSG} Tenho interesse no ${resort.name}.`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 bg-white/10 px-8 py-4 text-sm font-semibold text-white backdrop-blur-md transition-all duration-200 hover:border-wa hover:bg-wa active:scale-[0.98]"
                        >
                            <MessageCircle className="h-4 w-4" />
                            Falar no WhatsApp
                        </a>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 1, ease: EASE }}
                        className="mt-5 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-xs font-semibold text-gold backdrop-blur-md"
                        data-testid="resort-exclusive-note"
                    >
                        <BadgePercent className="h-4 w-4" />
                        Valores diferenciados para cotações feitas pelo Destino Resorts
                    </motion.p>
                </div>
            </section>

            {resort.extended ? (
                <>
                    <Marquee items={FAZZENDA_MARQUEE} />
                    <AwardsStrip />
                    <Experience />
                    <BoardPlan resort={resort} />
                    <Videos
                        videos={FAZZENDA_VIDEOS}
                        title="Sinta o Fazzenda antes de chegar"
                        intro="Vídeos oficiais do canal do resort: a cozinha que serve 7 refeições por dia, a rotina do rancho e as novas acomodações."
                    />
                    <Gallery />
                    <ForWhom />
                    <OfficialInfo />
                </>
            ) : (
                <>
                    <Marquee items={resort.marquee} />
                    <ResortGallery resort={resort} />
                    <ResortHighlights resort={resort} />
                    <BoardPlan resort={resort} />
                    <RichExperience data={resort.experience} />
                    <Videos
                        videos={resort.videos}
                        title={`Veja o ${resort.name} em vídeo`}
                        intro="Dê play e sinta o clima do destino antes de fechar sua cotação."
                    />
                    <ForWhom items={resort.audiences} />
                </>
            )}

            <QuoteForm
                sectionId="cotacao-resort"
                defaultResort={resort.slug}
                eyebrow="Cotação Deste Resort"
                heading="Solicite a cotação"
                intro={`Conte as datas e quem vai com você: a proposta personalizada para o ${resort.name}, com valores diferenciados, chega direto no seu WhatsApp.`}
            />
        </>
    );
}
